/**
 * Local Zustand store for the Flow editor.
 *
 * Kept separate from the Synth Rider editorStore so the two editors don't
 * trip over each other's state. Shared concerns (active view, theme,
 * sidebar widths) still live in editorStore.
 */

import { create } from 'zustand';
import type { ParsedVizProject } from '../../viz/vizproject';
import type { FlowChart, FlowNote, FlowLaneIndex, FlowNoteType, FlowLaneCount, FlowDifficultyName } from './types';
import { emptyChart, FLOW_LANE_COUNT, laneCountFor, migrateFlowDifficultyName } from './types';
import { DEFAULT_KEYBINDINGS, type FlowAction } from './flowKeybindings';
import { getApiBase } from '../../api/apiBase';
import {
  FlowGameplaySettings,
  FlowInterfaceSettings,
  FlowParticleSettings,
  DEFAULT_FLOW_GAMEPLAY,
  DEFAULT_FLOW_INTERFACE,
  DEFAULT_FLOW_PARTICLES,
  FlowPhoneSettings,
  DEFAULT_FLOW_PHONE,
  FlowSfxSettings,
  DEFAULT_FLOW_SFX,
  VisualizerScene,
  VizClip,
} from './flowSettings';

/**
 * Note IDs placed via Live Mapping that already played their hit sound on
 * key-press (and, for trails, on release). The strike-line crossing detector
 * in FlowScene must NOT re-sound these, or each live note would play twice.
 * Transient (never persisted); each id is consumed the first time the playhead
 * crosses it. Lives at module scope so FlowApp (writer) and FlowScene (reader)
 * share one instance without forcing re-renders.
 */
export const liveHitSuppress = new Set<string>();

/**
 * Which lanes (0..3) currently have their Live Mapping key (A/S/D/F) held down.
 * FlowApp sets these on key-down/up; FlowScene reads them each frame to light
 * the lane keypad ("pill") while the key is pressed — instant for a tap, held
 * for a trail. Module scope so the writer (FlowApp) and reader (FlowScene)
 * share one array without re-renders.
 */
export const liveLaneHeld: boolean[] = [false, false, false, false, false];

// ── Play Test run state ─────────────────────────────────────────────────────
// Module-local (not store state) so the per-frame renderer can read it without
// subscriptions and a key press never triggers a React render.

/** Notes the player HIT this run. Tap notes in here stop rendering — a hit note
 *  disappears; a missed one keeps flying past the strike line. */
export const playTestHitIds = new Set<string>();
/** Notes already judged this run (hit OR missed) — each note scores once. */
export const playTestJudgedIds = new Set<string>();
/** Hit sparks requested by the key handler, drained by the scene each frame. */
export const playTestSparkQueue: { lane: number; strong: boolean }[] = [];
/** Per-lane trail currently being held (set on hit, cleared on release/end) —
 *  drives the looping particles at the strike line while the key is down. */
export const playTestHolds: ({ noteId: string; endBeat: number } | null)[] =
  [null, null, null, null, null];

/** Wipe every per-run marker (entering or leaving a Play Test run). */
export function resetPlayTestRun(): void {
  playTestHitIds.clear();
  playTestJudgedIds.clear();
  playTestSparkQueue.length = 0;
  playTestHolds.fill(null);
}


// Visual/display settings types + defaults live in flowSettings.ts (extracted
// for modularity); re-exported so existing imports keep working. The matching
// `import { ... } from './flowSettings'` sits with the other top-of-file imports.
export * from './flowSettings';

interface FlowSelection {
  noteIds: Set<string>;
}

interface FlowState {
  // ── Chart data ────────────────────────────────────────────────────────
  chart: FlowChart;
  setChart: (chart: FlowChart) => void;
  addNote: (lane: FlowLaneIndex, time_beats: number, type?: FlowNoteType) => void;
  /** Batch-insert multiple notes in a single history entry + single store update.
   *  Use this instead of calling addNote() in a loop — addNote pushes one
   *  history snapshot and triggers one React re-render per note, which is
   *  unusable for large MIDI imports (500+ notes). */
  insertNotesBatch: (rawNotes: Array<{ lane: FlowLaneIndex; time_beats: number; type?: FlowNoteType }>) => void;
  removeNotes: (ids: string[]) => void;
  clearChart: () => void;

  // ── Drag interactions ─────────────────────────────────────────────────
  /** Take a single history snapshot before a multi-step interaction
   *  (e.g. an Alt+drag). The caller is responsible for invoking this
   *  exactly once at drag start so undo rewinds the whole drag in one shot
   *  instead of stepping through every pointermove tick. */
  beginEdit: () => void;
  /** Move a note's head position (lane + time). If the note has waypoints
   *  the first waypoint is moved along with the head so they stay in
   *  sync. Skips the history stack — pair with beginEdit() per drag. */
  moveNote: (id: string, lane: FlowLaneIndex, time_beats: number) => void;
  /** Move a single waypoint of a trail. Index 0 is the head — moving it
   *  also updates the note's `lane`/`time_beats` fields. Skips history. */
  moveWaypoint: (noteId: string, index: number, lane: FlowLaneIndex, time_beats: number) => void;

  // ── Loaded audio ──────────────────────────────────────────────────────
  audioUrl: string | null;
  audioName: string | null;
  audioLoading: boolean;
  audioError: string | null;
  /** Native sample rate of the loaded audio file (Hz). Populated by the
   *  backend's /api/audio/analyze response — falls back to 44100 if missing. */
  audioFrequency: number;
  /** Number of audio channels (1 = mono, 2 = stereo). */
  audioChannels: number;
  /** Total number of audio samples (frames × channels). */
  audioSamples: number;
  setAudio: (params: {
    url: string;
    name: string;
    bpm: number;
    offsetSeconds: number;
    durationSeconds: number;
    audioFrequency?: number;
    audioChannels?: number;
    audioSamples?: number;
  }) => void;

  // ── Song metadata (shown in the saved JSON, editable from the UI) ────
  /** Display name of the song. */
  songName: string;
  setSongName: (name: string) => void;
  /** Display name of the song's author / artist. */
  songAuthor: string;
  setSongAuthor: (name: string) => void;
  setAudioLoading: (loading: boolean) => void;
  setAudioError: (msg: string | null) => void;

  // ── Cover art ─────────────────────────────────────────────────────────
  /** Blob URL of the currently loaded cover image (or null if none). */
  coverUrl: string | null;
  /** Original filename (including extension) of the cover. */
  coverName: string | null;
  /** MIME type of the loaded cover, used when re-packing the bundle. */
  coverMime: string | null;
  /** Raw bytes of the cover — held in memory so save() can re-pack the
   *  bundle without needing the user to re-pick the image. */
  coverBytes: ArrayBuffer | null;
  setCover: (params: {
    url: string;
    name: string;
    mime: string;
    bytes: ArrayBuffer;
  }) => void;
  clearCover: () => void;

  // ── Playback ──────────────────────────────────────────────────────────
  currentTime: number;
  currentBeat: number;
  playing: boolean;
  duration: number;
  playbackSpeed: number;
  /** Passive time update — used by the rAF loop that mirrors audio.currentTime
   *  into the store. Does NOT seek the audio element, so calling this while
   *  playing is a no-op visually (next rAF tick overwrites it). */
  setCurrentTime: (t: number) => void;
  setPlaying: (p: boolean) => void;
  setDuration: (d: number) => void;
  setPlaybackSpeed: (s: number) => void;
  /** User-initiated jump. Seeks the registered audio element (if any) so the
   *  change sticks during playback, then updates the store. Use this for
   *  wheel-scrub, timeline drag, transport buttons — anything the user does
   *  intentionally to change position. */
  seek: (t: number) => void;
  /** Called once by the audio host (FlowApp) so seek() can move the real
   *  HTMLAudioElement. Pass null on unmount. */
  registerAudioSeek: (fn: ((t: number) => void) | null) => void;

  // ── Mixer (0..1) ──────────────────────────────────────────────────────
  /** Output volume of the music audio element. */
  musicVolume: number;
  setMusicVolume: (v: number) => void;
  /** Volume of scrub/step UI ticks (forwarded to tickSound.setTickVolume). */
  sfxVolume: number;
  setSfxVolume: (v: number) => void;

  // ── A/V sync calibration ──────────────────────────────────────────────
  /**
   * Audio-output latency compensation, in milliseconds. During playback the
   * visual time is computed as `audio.currentTime − syncOffsetMs/1000`, so a
   * POSITIVE value delays the notes (use it when notes reach the strike bar
   * *before* you hear the sound — the usual case, since speakers/Bluetooth lag
   * behind the reported audio clock). Persisted; only applied while playing.
   */
  syncOffsetMs: number;
  setSyncOffsetMs: (ms: number) => void;

  /** Timestamp (ms) of the last rejected note-placement in the offset zone.
   *  The editor shows a transient "can't place here" message when this bumps. */
  offsetWarnAt: number;
  warnOffsetPlacement: () => void;

  /** Timestamp (ms) of the last edit blocked by the edit-window lock (or by
   *  Play Test). Flashes the same style of transient message. */
  editBlockWarnAt: number;
  warnEditBlocked: () => void;

  // ── Camera presets ────────────────────────────────────────────────────
  /** `default` = front-of-stage angle, `left`/`right` = side cuts,
   *  `free` = orbit/pan with the mouse, `custom` = a user pose. */
  cameraView: 'default' | 'left' | 'right' | 'free' | 'custom';
  setCameraView: (v: FlowState['cameraView']) => void;
  /** Per-view editable pose overrides (position + euler rotation). `null` on a
   *  field means "use the built-in pose" (see flowCameraPresets). Each is
   *  directly editable via the Camera settings sliders and persisted. */
  defaultCameraPosition: [number, number, number] | null;
  defaultCameraRotation: [number, number, number] | null;
  leftCameraPosition: [number, number, number] | null;
  leftCameraRotation: [number, number, number] | null;
  rightCameraPosition: [number, number, number] | null;
  rightCameraRotation: [number, number, number] | null;
  customCameraPosition: [number, number, number] | null;
  customCameraRotation: [number, number, number] | null;
  setDefaultCameraPosition: (p: [number, number, number]) => void;
  setDefaultCameraRotation: (r: [number, number, number]) => void;
  setLeftCameraPosition: (p: [number, number, number]) => void;
  setLeftCameraRotation: (r: [number, number, number]) => void;
  setRightCameraPosition: (p: [number, number, number]) => void;
  setRightCameraRotation: (r: [number, number, number]) => void;
  setCustomCameraPosition: (p: [number, number, number]) => void;
  setCustomCameraRotation: (r: [number, number, number]) => void;
  /** Reset a preset's override back to the built-in pose. */
  resetCameraPreset: (preset: 'default' | 'left' | 'right' | 'custom') => void;
  clearCustomCamera: () => void;
  /** Capture the live free-orbit pose into the custom view (bonus path used by
   *  the "save current view" button / the CameraController save watcher). */
  saveCameraRequest: number; // monotonic counter; bump to trigger
  requestSaveCustomCamera: () => void;
  storeCustomCamera: (pos: [number, number, number], rot: [number, number, number]) => void;

  // ── Editor settings ────────────────────────────────────────────────────
  snapDivision: number;
  setSnapDivision: (n: number) => void;
  /** Note Jump Speed — editor-view multiplier on the beat→distance spacing.
   *  Higher = notes more spread out / approach faster in the 3D view (like the
   *  SR editor's NJS). 1 = the base spacing. Editor-only; doesn't change timing. */
  flowNjs: number;
  setFlowNjs: (n: number) => void;
  /** User-rebindable keyboard shortcuts (action id → combo string). */
  keybindings: Record<FlowAction, string>;
  setKeybinding: (action: FlowAction, combo: string) => void;
  resetKeybindings: () => void;
  /** Minimap camera pose (height = Y, posZ = forward/back, tilt = look-at depth).
   *  Tunable live via the floating "Minimap Camera" window; persisted. */
  minimapCam: { height: number; posZ: number; tilt: number };
  setMinimapCam: (partial: Partial<{ height: number; posZ: number; tilt: number }>) => void;
  resetMinimapCam: () => void;
  /** Whether the floating minimap-camera adjustment window is open (transient). */
  minimapCamWinOpen: boolean;
  setMinimapCamWinOpen: (open: boolean) => void;
  /** Editor Settings dialog — toggled from the top-right header button. */
  settingsOpen: boolean;
  setSettingsOpen: (open: boolean) => void;
  /** Which Settings tab is showing. In the store so other UI (e.g. the shortcut
   *  sheet's "Rebind in Settings → Keys") can deep-link straight to a tab. */
  settingsTab: string;
  setSettingsTab: (tab: string) => void;
  /** When true, a tick sound fires on each whole-beat crossing during
   *  playback — the metronome function of the transport panel. */
  metronomeEnabled: boolean;
  setMetronomeEnabled: (v: boolean) => void;
  /** Show the difficulty-complexity overlay (amber/red bands flagging sections
   *  too hard for the active tier) on both the timeline and the 3D highway. */
  showComplexity: boolean;
  setShowComplexity: (v: boolean) => void;
  /** Show the A/V sync debug console (the on-screen sampler panel) and stream
   *  the timing probe to the browser console. Toggled by Ctrl+Shift+D or the
   *  transport's debug button. */
  debugSync: boolean;
  setDebugSync: (v: boolean) => void;
  /** Right-drag loop region (in beats). When set and playing, playback wraps
   *  back to startBeat once it passes endBeat — looping that section. Drawn
   *  teal on the floor + timeline. null = no loop. */
  loopRegion: { startBeat: number; endBeat: number } | null;
  setLoopRegion: (r: { startBeat: number; endBeat: number } | null) => void;
  clearLoopRegion: () => void;
  /** A selected time SECTION (beat range) from the timeline area-drag — set even
   *  when the range contains no notes, so "Map this section" knows what to map.
   *  Independent of the note selection. null = none. */
  selectedSection: { startBeat: number; endBeat: number } | null;
  setSelectedSection: (r: { startBeat: number; endBeat: number } | null) => void;
  /** Shortcut hint bar hidden outright (✕) — re-enable in Settings → UI. */
  hintBarHidden: boolean;
  setHintBarHidden: (v: boolean) => void;
  /** Play Test: play the map in-editor with the lane keys.
   *  Shift+Space = PRACTICE (no score, scrub anywhere and keep playing),
   *  Ctrl+Shift+Space = SCORED run (starts at 0:00, no going back). */
  playTest: boolean;
  /** True only for the scored run — drives the hit/miss HUD and the no-rewind
   *  lock. Practice mode leaves both off. */
  playTestScored: boolean;
  setPlayTest: (v: boolean, scored?: boolean) => void;
  playTestStats: { hits: number; misses: number };
  bumpPlayTest: (kind: 'hit' | 'miss') => void;
  /** Edit-window lock: when ON, placement/deletion clicks only work within
   *  ±EDIT_WINDOW_BEATS of the playhead (E toggles; guards far-away notes). */
  editWindowEnabled: boolean;
  setEditWindowEnabled: (v: boolean) => void;
  /** Live Mapping: record notes in real time with the A/S/D/F keys while the
   *  song is playing (tap = note, hold→release = trail). */
  liveMapping: boolean;
  setLiveMapping: (v: boolean) => void;
  /** Add a note during live mapping; duration_beats > 0 makes it a trail.
   *  Returns the new note's id so the caller can extend it into a trail on
   *  key-up (see liveExtendNote). */
  /** Returns the new note's id, or null when the ≤2-per-beat cap rejected it. */
  liveAddNote: (lane: FlowLaneIndex, time_beats: number, duration_beats: number, type?: FlowNoteType) => string | null;
  /** Live mapping: stretch an already-placed note into a trail when the key is
   *  held past the trail threshold. No-op if the id no longer exists. */
  liveExtendNote: (id: string, duration_beats: number) => void;
  activeDifficulty: FlowDifficultyName;
  setActiveDifficulty: (d: FlowDifficultyName) => void;
  /** Create an empty difficulty (default or custom-named) and switch to it. */
  addDifficulty: (name: string) => void;
  /** Used by chart-load paths only: assumes `chart` is already arranged with
   *  `notes` holding the right diff's notes and `difficultyNotes` holding the
   *  rest. Skips the swap that the user-facing setter does. */
  setActiveDifficultyAfterLoad: (d: FlowDifficultyName) => void;
  /** Change the ACTIVE difficulty's lane count (3 ↔ 4). Existing notes are
   *  remapped proportionally across the new lane count (and de-duplicated when
   *  two collapse onto the same cell). One undo step. */
  setActiveLaneCount: (count: FlowLaneCount) => void;

  /** Edit-map-info dialog open flag. Lives in the store (instead of FlowApp
   *  state) so the SongInfoBanner inside FlowScene can flip it without prop
   *  drilling through the scene's render tree. */
  editMapInfoOpen: boolean;
  setEditMapInfoOpen: (open: boolean) => void;
  noteTypeBrush: FlowNoteType | 'trail';
  setNoteTypeBrush: (t: FlowNoteType | 'trail') => void;
  /** When true, notes/trails placed in the editor are mirrored across the
   *  centre line (lane L also creates one at FLOW_LANE_COUNT-1-L). */
  noteMirror: boolean;
  setNoteMirror: (v: boolean) => void;

  // ── Trail placement (multi-waypoint flow) ──────────────────────────────
  /** When non-null we're editing a trail in progress — the note id of the
   *  head. Subsequent floor clicks ADD waypoints; Enter commits, ESC
   *  cancels (discards the head). */
  pendingTrailNoteId: string | null;
  startTrail: (noteId: string) => void;
  /** Append a waypoint (lane + beat) to the in-progress trail. The new
   *  waypoint's time_beats must be strictly greater than the previous
   *  waypoint's time, otherwise the call is a no-op. */
  addTrailWaypoint: (lane: FlowLaneIndex, time_beats: number) => void;
  /** Remove ONE intermediate waypoint from a committed trail (head/tail stay). */
  removeTrailWaypoint: (noteId: string, wpIndex: number) => void;
  /** Finalize the in-progress trail. Requires ≥ 2 waypoints (head + at
   *  least one extension); otherwise the trail collapses back to a single
   *  tap on the head's lane. */
  commitTrail: () => void;
  cancelTrail: () => void;

  // ── History (undo) ─────────────────────────────────────────────────────
  /** True iff there's at least one snapshot we can pop. UI uses this
   *  to enable/disable an Undo button. */
  canUndo: boolean;
  /** Pop the most recent chart snapshot back into play. No-op if empty. */
  undo: () => void;
  /** True iff undo() has run and no new edit has invalidated the branch. */
  canRedo: boolean;
  /** Re-apply the change undone by undo() (Ctrl+Y). No-op if empty. */
  redo: () => void;

  // ── Selection ──────────────────────────────────────────────────────────
  selection: FlowSelection;
  selectNotes: (ids: string[], additive?: boolean) => void;
  /** Remove the given ids from the selection (used by Ctrl+right-drag). */
  deselectNotes: (ids: string[]) => void;
  clearSelection: () => void;
  /** Select every note in the chart. */
  selectAllNotes: () => void;
  /** Remove every currently-selected note. */
  deleteSelection: () => void;
  /** Snapshot the selected notes into the in-memory clipboard. */
  copySelection: () => void;
  /**
   * Mark the selected notes as "cut" — they get copied to the clipboard
   * AND rendered at reduced opacity. They stay in the chart until the
   * next paste consumes them, or until clearCut() / Esc cancels the cut.
   */
  cutSelection: () => void;
  /** Note ids currently flagged as cut. Render-side reads this to dim them. */
  cutNoteIds: Set<string>;
  /** Clear the cut marker without deleting anything. */
  clearCut: () => void;
  /**
   * Paste the clipboard relative to currentBeat: the earliest clipboard
   * note lands at currentBeat, later notes preserve their offsets.
   * Newly added notes become the selection.
   */
  pasteAtPlayhead: () => void;
  /** Paste the clipboard at the playhead MIRRORED across the centre line
   *  (lanes + trail waypoints flipped) — the desktop editor's Alt+V. */
  pasteMirrorAtPlayhead: () => void;
  /** Add a bookmark at the playhead, or remove the one already there. */
  toggleBookmarkAtPlayhead: () => void;
  /** Set (or clear, with '') the label of the bookmark at this beat. */
  renameBookmark: (time_beats: number, label: string) => void;
  /** Seek to the next (+1) / previous (-1) bookmark from the playhead. */
  jumpBookmark: (dir: 1 | -1) => void;
  /**
   * Shift every selected note by `deltaBeats`. Clamped so the earliest
   * selected note never crosses below 0.
   */
  shiftSelectionBy: (deltaBeats: number) => void;
  /** Move the selected notes by `delta` lanes (-1 left / +1 right), clamped so
   *  no selected note (head or trail waypoint) leaves the 0..3 lane range. */
  shiftSelectionLane: (delta: number) => void;
  /** Has anything been copied this session? UI uses it to gate buttons. */
  clipboardCount: number;

  // ── Visual settings ──────────────────────────────────────────────────────
  gameplaySettings: FlowGameplaySettings;
  setGameplaySettings: (patch: Partial<FlowGameplaySettings>) => void;
  resetGameplaySettings: () => void;

  flowInterfaceSettings: FlowInterfaceSettings;
  setFlowInterfaceSettings: (patch: Partial<FlowInterfaceSettings>) => void;
  resetFlowInterfaceSettings: () => void;

  particleSettings: FlowParticleSettings;
  setParticleSettings: (patch: Partial<FlowParticleSettings>) => void;
  resetParticleSettings: () => void;

  phoneSettings: FlowPhoneSettings;
  setPhoneSettings: (patch: Partial<FlowPhoneSettings>) => void;
  resetPhoneSettings: () => void;

  sfxSettings: FlowSfxSettings;
  setSfxSettings: (patch: Partial<FlowSfxSettings>) => void;
  resetSfxSettings: () => void;

  /** Loaded .visualizer scene (session-only, not persisted). With a .vizproject
   *  timeline loaded this is driven by the active clip at the current time. */
  visualizerScene: VisualizerScene | null;
  setVisualizerScene: (scene: VisualizerScene | null) => void;

  /** Loaded .vizproject timeline — scene clips with absolute start/duration (s).
   *  When present, the active clip's scene is what the renderer shows. */
  visualizerTimeline: VizClip[] | null;
  visualizerProjectName: string | null;
  /** Load a .vizproject timeline (clears the single scene); null clears it. */
  setVisualizerTimeline: (clips: VizClip[] | null, name?: string | null) => void;

  /** Parsed .vizproject for the REAL ported Visualizer Studio engine (full
   *  project + rehosted media object-URLs). Session-only; null = none.
   *  Rendered by VizProjectHost. */
  vizProject: ParsedVizProject | null;
  setVizProject: (p: ParsedVizProject | null) => void;

  /** Library of imported visuals (kept loaded, grouped scene/project). The raw
   *  JSON of each lives in IndexedDB; this is just the metadata for the list. */
  vizLibrary: VizLibItem[];
  setVizLibrary: (items: VizLibItem[]) => void;
  /** Which library item is currently active (rendered). */
  activeVizId: string | null;
  setActiveVizId: (id: string | null) => void;
}

/** One imported visual in the library. */
export interface VizLibItem {
  id: string;
  name: string;
  kind: 'scene' | 'project';
}

// ── Settings persistence (localStorage) ──────────────────────────────────────
// Settings are serialised to localStorage so they survive browser refreshes,
// tab closes, and navigation. The store initialises from the saved values on
// first load and writes back on every setter call (including resets).
//
// Key prefix: "synapse:flow:settings:"
// These keys can be exported to a JSON file and committed to git if desired;
// see FlowSettingsDialog's "Export / Import" buttons (to be added later).

const SETTINGS_KEYS = {
  gameplay:  'synapse:flow:settings:gameplay',
  interface: 'synapse:flow:settings:interface',
  particles: 'synapse:flow:settings:particles',
  mixer:     'synapse:flow:settings:mixer',
  sync:      'synapse:flow:settings:sync',
  camera:    'synapse:flow:settings:camera',
  phone:     'synapse:flow:settings:phone',
  sfx:       'synapse:flow:settings:sfx',
  visualizer: 'synapse:flow:settings:visualizer',
} as const;

interface FlowMixerSettings { musicVolume: number; sfxVolume: number }
const DEFAULT_FLOW_MIXER: FlowMixerSettings = { musicVolume: 0.58, sfxVolume: 1 };

interface FlowSyncSettings { syncOffsetMs: number }
const DEFAULT_FLOW_SYNC: FlowSyncSettings = { syncOffsetMs: 0 };

/** Persisted per-view camera pose overrides. null on a field = use built-in. */
type V3 = [number, number, number];
interface FlowCameraSettings {
  /** Bumped when the shipped default view changes — see the migration below. */
  v?: number;
  defaultPos: V3 | null; defaultRot: V3 | null;
  leftPos: V3 | null;    leftRot: V3 | null;
  rightPos: V3 | null;   rightRot: V3 | null;
  customPos: V3 | null;  customRot: V3 | null;
}
/** Current camera-settings schema. Bump to re-force the built-in views. */
const CAMERA_SETTINGS_VERSION = 2;
/** No overrides — every view falls back to BUILTIN_CAMERA. */
const DEFAULT_FLOW_CAMERA: FlowCameraSettings = {
  v: CAMERA_SETTINGS_VERSION,
  defaultPos: null, defaultRot: null,
  leftPos:    null, leftRot:    null,
  rightPos:   null, rightRot:   null,
  customPos:  null, customRot:  null,
};
/** The junk Custom pose v1 shipped to everyone (someone's saved bird's-eye
 *  view baked into the defaults). Dropped on migration — but only if it is
 *  still untouched, so a real user-saved Custom survives. */
const V1_STALE_CUSTOM_Y = 45.316900175426504;

// Loaded once at module init; seeds the camera pose fields below.
const _camSaved = ((): FlowCameraSettings => {
  const saved = loadSettings<FlowCameraSettings>(SETTINGS_KEYS.camera, DEFAULT_FLOW_CAMERA);
  // Read the stamp off the RAW payload: loadSettings spreads the defaults
  // underneath, which would otherwise hand every old blob the current version.
  let storedVersion: unknown;
  try { storedVersion = JSON.parse(localStorage.getItem(SETTINGS_KEYS.camera) || '{}')?.v; } catch { /* unreadable → migrate */ }
  if (storedVersion === CAMERA_SETTINGS_VERSION) return saved;
  // v1 (no version stamp): the Default/Sides poses came from a hard-coded
  // fallback, and Reset dropped users onto a built-in that faced away from the
  // track. Force the new, verified views onto every install exactly once; a
  // genuinely user-saved Custom pose is the only thing carried over.
  const keepCustom = saved.customPos != null && saved.customPos[1] !== V1_STALE_CUSTOM_Y;
  return {
    ...DEFAULT_FLOW_CAMERA,
    customPos: keepCustom ? saved.customPos : null,
    customRot: keepCustom ? saved.customRot : null,
  };
})();
/** Persist every camera pose override from the current store state. */
function persistCamera(get: () => FlowState): void {
  const s = get();
  saveSettings(SETTINGS_KEYS.camera, {
    v: CAMERA_SETTINGS_VERSION,
    defaultPos: s.defaultCameraPosition, defaultRot: s.defaultCameraRotation,
    leftPos: s.leftCameraPosition,       leftRot: s.leftCameraRotation,
    rightPos: s.rightCameraPosition,     rightRot: s.rightCameraRotation,
    customPos: s.customCameraPosition,   customRot: s.customCameraRotation,
  });
}

// Persisted loaded visualizer (single .visualizer scene OR a .vizproject
// timeline) so a browser refresh doesn't wipe it. Media isn't stored — only the
// scene params / timeline clips, which are small.
interface PersistedViz { scene: VisualizerScene | null; timeline: VizClip[] | null; projectName: string | null }
const _vizSaved = loadSettings<PersistedViz>(
  SETTINGS_KEYS.visualizer, { scene: null, timeline: null, projectName: null },
);
function saveViz(v: PersistedViz): void {
  saveSettings(SETTINGS_KEYS.visualizer, v);
}

function loadSettings<T extends object>(key: string, defaults: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return defaults;
    // Spread defaults first so newly-added fields get their default values
    // even when an older persisted object doesn't have them yet.
    return { ...defaults, ...JSON.parse(raw) } as T;
  } catch {
    return defaults;
  }
}

function saveSettings(key: string, value: object): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    localStorage.setItem(SAVED_AT_KEY, String(Date.now()));
  } catch { /* quota / private */ }
  scheduleFlowServerPush();
}

// ── Server-side settings sync ────────────────────────────────────────────────
// Mirrors the SR editor's /api/prefs backup: the Flow settings (lane colours,
// SFX/scroll sounds, particles, camera, etc.) live in localStorage but are also
// pushed to the backend so they survive browser switches / cleared site data.
// Last-write-wins via a _savedAt timestamp — on boot we adopt the server copy
// only when it's strictly newer than this browser's, so a recent local change
// is never clobbered by an older server snapshot.

const FLOW_PREFS_URL = `${getApiBase()}/api/prefs/flow`;
const SAVED_AT_KEY = 'synapse:flow:_savedAt';
const FLOW_SYNC_DEBOUNCE_MS = 1500;
let _flowSyncTimer: ReturnType<typeof setTimeout> | null = null;
let _flowLastSent = '';

/** Collect every persisted Flow setting (the `synapse:flow:` keys, minus the
 *  bookkeeping timestamp) from localStorage into one blob. */
function gatherFlowPrefs(): Record<string, string> {
  const out: Record<string, string> = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('synapse:flow:') && k !== SAVED_AT_KEY) {
        const v = localStorage.getItem(k);
        if (v != null) out[k] = v;
      }
    }
  } catch { /* ignore */ }
  return out;
}

/** Debounced POST of all Flow settings to the backend. */
function scheduleFlowServerPush(): void {
  if (typeof window === 'undefined') return;
  if (_flowSyncTimer) clearTimeout(_flowSyncTimer);
  _flowSyncTimer = setTimeout(() => {
    const savedAt = Number(localStorage.getItem(SAVED_AT_KEY) || '0');
    const blob = JSON.stringify({ _savedAt: savedAt, keys: gatherFlowPrefs() });
    if (blob === _flowLastSent) return;
    fetch(FLOW_PREFS_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prefs: blob }),
    }).then(res => { if (res.ok) _flowLastSent = blob; })
      .catch(() => { /* offline / backend down — retry on next save */ });
  }, FLOW_SYNC_DEBOUNCE_MS);
}

/** On boot, adopt the server copy when it's newer than the local one (or when
 *  this browser has no Flow settings yet). Writes localStorage + reloads once so
 *  the synchronous store init picks up the recovered values. */
function hydrateFlowFromServer(): void {
  if (typeof window === 'undefined') return;
  fetch(FLOW_PREFS_URL)
    .then(res => (res.ok ? res.json() : null))
    .then(payload => {
      if (!payload || typeof payload.prefs !== 'string') return;
      let server: { _savedAt?: number; keys?: Record<string, string> };
      try { server = JSON.parse(payload.prefs); } catch { return; }
      if (!server.keys || typeof server.keys !== 'object') return;

      const localAt = Number(localStorage.getItem(SAVED_AT_KEY) || '0');
      const serverAt = Number(server._savedAt || 0);
      const hasLocal = Object.keys(gatherFlowPrefs()).length > 0;
      if (hasLocal && serverAt <= localAt) return; // local is newer/equal — keep it

      for (const [k, v] of Object.entries(server.keys)) {
        if (k.startsWith('synapse:flow:') && typeof v === 'string') localStorage.setItem(k, v);
      }
      localStorage.setItem(SAVED_AT_KEY, String(serverAt));
      // eslint-disable-next-line no-console
      console.info('[flow-prefs] Recovered Flow settings from backend, reloading…');
      window.location.reload();
    })
    .catch(() => { /* backend offline — keep local */ });
}

// Kick off hydration on module load; also back up existing local settings once
// so the first session is captured without waiting for a setting to change.
if (typeof window !== 'undefined') {
  hydrateFlowFromServer();
  if (Object.keys(gatherFlowPrefs()).length > 0) scheduleFlowServerPush();
}

let _idSeed = 1;
function makeId(): string { return `fn-${_idSeed++}`; }

/** Map a lane index from one lane count to another by RELATIVE POSITION, so the
 *  pattern keeps its shape. e.g. 4→3: 0→0,1→1,2→1,3→2; 3→4: 0→0,1→2,2→3. */
function remapLane(lane: number, oldCount: number, newCount: number): FlowLaneIndex {
  if (oldCount <= 1 || newCount <= 1) return 0 as FlowLaneIndex;
  const mapped = Math.round((lane * (newCount - 1)) / (oldCount - 1));
  return Math.max(0, Math.min(newCount - 1, mapped)) as FlowLaneIndex;
}

/** Remap every note (and trail waypoint) from oldCount→newCount lanes. Notes
 *  that collapse onto the same (lane, beat) cell are de-duplicated, keeping the
 *  first — the chart may need manual touch-up, which the UI warns about. */
function remapNotesToLaneCount(notes: FlowNote[], oldCount: number, newCount: number): FlowNote[] {
  const seen = new Set<string>();
  const out: FlowNote[] = [];
  for (const n of notes) {
    const lane = remapLane(n.lane, oldCount, newCount);
    const key = `${lane}@${n.time_beats.toFixed(4)}`;
    if (seen.has(key)) continue;          // collision → drop the duplicate
    seen.add(key);
    const waypoints = n.waypoints && n.waypoints.length >= 2
      ? n.waypoints.map(w => ({ lane: remapLane(w.lane, oldCount, newCount), time_beats: w.time_beats }))
      : n.waypoints;
    out.push({ ...n, lane, waypoints });
  }
  return out;
}

/**
 * Offset-aware beat ↔ time conversion (matches the SR editor's model).
 *
 * `offset` is the song's lead-in in SECONDS — the audio time at which musical
 * beat 0 lands. Subtracting it everywhere keeps notes locked to the actual
 * music instead of to the raw start of the audio file. Both helpers clamp the
 * bpm so a zero/garbage tempo can't divide-by-zero.
 */
function beatToTime(beat: number, bpm: number, offset: number): number {
  return (beat / Math.max(1, bpm)) * 60 + offset;
}
function timeToBeat(time: number, bpm: number, offset: number): number {
  return ((time - offset) / 60) * Math.max(1, bpm);
}

/** Audio-seek callback registered by FlowApp. Module-local because there's
 *  only ever one audio element per editor instance. */
let _audioSeekFn: ((t: number) => void) | null = null;

/** In-memory clipboard: snapshots of the notes copied via copySelection().
 *  Module-local because we only ever need one clipboard at a time and we
 *  don't want it to participate in Zustand's render-on-change tracking. */
let _clipboard: FlowNote[] = [];

// ── Undo history ──────────────────────────────────────────────────────────
/** Hard cap on the number of snapshots kept in memory. Oldest are dropped
 *  past the cap. 200 covers a long editing session without bloating memory. */
const HISTORY_LIMIT = 200;

interface HistoryEntry {
  chart: FlowChart;
  /** Which difficulty was active at the time of the snapshot. Needed so
   *  undo across a `setActiveDifficulty` rewinds the UI back to that diff
   *  instead of staying on the new one and silently swapping the notes. */
  activeDifficulty: FlowDifficultyName;
}

/** Module-local history stack: a list of chart+diff snapshots in chronological
 *  order. The current chart is NOT in this list; the list holds what the
 *  state was *before* each mutating action. */
let _history: HistoryEntry[] = [];
/** Redo stack: filled by undo(), consumed by redo(), CLEARED by any new edit
 *  (pushHistory) — the classic branch-discard model every editor uses. */
const _redoStack: HistoryEntry[] = [];

/** Take a shallow snapshot suitable for restoring later. Notes are
 *  cloned because individual note objects may be mutated in place by some
 *  paths (trail completion currently rebuilds the array, but defensive
 *  cloning is cheap and prevents future foot-guns). */
/** How many notes' HEADS sit on this beat (±0.02), across all lanes. Used to
 *  cap same-beat stacking at 2 (user feedback: more than two is unplayable). */
function notesAtBeat(notes: FlowNote[], beat: number): number {
  let count = 0;
  for (const n of notes) if (Math.abs(n.time_beats - beat) < 0.02) count++;
  return count;
}

/** Merge pasted notes into the chart WITHOUT letting the paste smuggle in
 *  what direct placement forbids (reviewer catch: copy/paste circumvented the
 *  2-notes-per-beat cap). Fresh notes are admitted one by one, oldest beat
 *  first; one that would land on an occupied lane+beat cell or push a beat
 *  past 2 heads is silently dropped — same feel as placement refusing. */
function mergeRespectingCaps(surviving: FlowNote[], fresh: FlowNote[]): { merged: FlowNote[]; kept: FlowNote[] } {
  const pool = [...surviving];
  const kept: FlowNote[] = [];
  for (const n of [...fresh].sort((a, b) => a.time_beats - b.time_beats)) {
    const dupe = pool.some(p => p.lane === n.lane && Math.abs(p.time_beats - n.time_beats) < 0.02);
    if (dupe || notesAtBeat(pool, n.time_beats) >= 2) continue;
    pool.push(n);
    kept.push(n);
  }
  return { merged: pool.sort((a, b) => a.time_beats - b.time_beats), kept };
}

function snapshotChart(c: FlowChart): FlowChart {
  // Deep-ish clone — every note becomes a fresh object, and the per-diff
  // snapshot map is rebuilt with copies of each note so undo never aliases
  // back into the live chart.
  const dn: Partial<Record<keyof NonNullable<FlowChart['difficultyNotes']>, FlowNote[]>> = {};
  if (c.difficultyNotes) {
    for (const k of Object.keys(c.difficultyNotes) as Array<keyof NonNullable<FlowChart['difficultyNotes']>>) {
      const arr = c.difficultyNotes[k];
      if (arr) dn[k] = arr.map(n => ({ ...n }));
    }
  }
  return {
    bpm: c.bpm,
    offset_seconds: c.offset_seconds,
    notes: c.notes.map(n => ({ ...n })),
    difficultyNotes: dn,
    // laneCounts was silently dropped here before — an undo then reverted every
    // difficulty to the 4-lane default. Carry it (and bookmarks) through.
    laneCounts: c.laneCounts ? { ...c.laneCounts } : undefined,
    bookmarks: c.bookmarks ? c.bookmarks.map(b => ({ ...b })) : undefined,
    meta: c.meta ? { ...c.meta } : undefined,
  };
}

/** Migrate a freshly-loaded chart's legacy 'Medium' difficulty → 'Normal'
 *  (across difficultyNotes + laneCounts). Returns the SAME object reference
 *  when there's nothing to migrate, so in-place tempo edits aren't misread as
 *  fresh loads (setChart relies on reference equality for that). */
function migrateChartDiffNames(chart: FlowChart): FlowChart {
  const hasDn = chart.difficultyNotes && 'Medium' in chart.difficultyNotes;
  const hasLc = chart.laneCounts && 'Medium' in chart.laneCounts;
  if (!hasDn && !hasLc) return chart;
  const out: FlowChart = { ...chart };
  if (hasDn) {
    const dn: Partial<Record<string, FlowNote[]>> = { ...(chart.difficultyNotes ?? {}) };
    if (dn['Normal'] == null && dn['Medium']) dn['Normal'] = dn['Medium'];
    delete dn['Medium'];
    out.difficultyNotes = dn;
  }
  if (hasLc) {
    const lc: Partial<Record<string, FlowLaneCount>> = { ...(chart.laneCounts ?? {}) };
    if (lc['Normal'] == null && lc['Medium']) lc['Normal'] = lc['Medium'];
    delete lc['Medium'];
    out.laneCounts = lc;
  }
  return out;
}

/** Push a snapshot onto the history stack, trimming the oldest entries
 *  once we exceed HISTORY_LIMIT. The active difficulty is read from the
 *  live store so callers don't need to remember to pass it. */
function pushHistory(c: FlowChart): void {
  // Inline import would cause a circular ref; read off the create()'d store
  // via its module-level singleton. Safe at call-time because the store has
  // long been initialised by the time any mutation fires.
  const activeDifficulty = useFlowStore.getState().activeDifficulty;
  _history.push({ chart: snapshotChart(c), activeDifficulty });
  if (_history.length > HISTORY_LIMIT) {
    // Drop the oldest entries beyond the cap. `splice` is fine — happens
    // at most once per action and the array is small.
    _history.splice(0, _history.length - HISTORY_LIMIT);
  }
  // A fresh edit invalidates the redo branch.
  if (_redoStack.length) {
    _redoStack.length = 0;
    // Reflect it in the UI flag without forcing extra renders elsewhere.
    queueMicrotask(() => useFlowStore.setState({ canRedo: false }));
  }
}

/** Heal an incoming notes array:
 *   1. Reassign any duplicate ids (older drafts on disk can contain them
 *      from a prior bug where _idSeed reset on reload).
 *   2. Advance _idSeed past every `fn-N` id so newly added notes don't
 *      collide with what's already in the chart.
 *  Returns the deduped notes (new array iff anything changed). */
function healNoteIds<T extends { id: string }>(notes: T[]): T[] {
  let max = _idSeed - 1;
  for (const n of notes) {
    const m = /^fn-(\d+)$/.exec(n.id);
    if (m) {
      const v = parseInt(m[1], 10);
      if (v > max) max = v;
    }
  }
  _idSeed = max + 1;

  const seen = new Set<string>();
  let changed = false;
  const out = notes.map(n => {
    if (!seen.has(n.id)) {
      seen.add(n.id);
      return n;
    }
    changed = true;
    const fresh = makeId();
    seen.add(fresh);
    return { ...n, id: fresh };
  });
  return changed ? out : notes;
}

export const useFlowStore = create<FlowState>((set, get) => ({
  chart: emptyChart(120),
  setChart: (chart) => {
    // setChart is used for both fresh-load (replace) and in-place edits
    // (BPM stepper, offset stepper). Snapshot the previous chart so the
    // user can undo a tempo tweak the same way they undo a note edit.
    const prev = get().chart;
    // Legacy 'Medium' → 'Normal' (no-op + same reference when not present).
    chart = migrateChartDiffNames(chart);
    pushHistory(prev);
    // Heal active-difficulty notes AND every snapshotted difficulty so an
    // incoming chart can't smuggle duplicate ids that collide with our
    // running _idSeed. Snapshots are processed first so the seed gets bumped
    // past every id before we touch the active set.
    const dn = chart.difficultyNotes ?? {};
    const healedDn: typeof dn = {};
    let dnChanged = false;
    for (const k of Object.keys(dn) as Array<keyof typeof dn>) {
      const arr = dn[k] ?? [];
      const h = healNoteIds(arr);
      healedDn[k] = h;
      if (h !== arr) dnChanged = true;
    }
    const healed = healNoteIds(chart.notes);
    let finalChart: FlowChart =
      healed === chart.notes && !dnChanged
        ? chart
        : { ...chart, notes: healed, difficultyNotes: healedDn };

    // ── In-place tempo / offset edit → MUSICAL TIME (notes anchored to beats) ─
    // Standard DAW behaviour (Cubase/Ableton "musical" mode): a note keeps its
    // musical position — its BEAT — when the tempo/offset changes; only its
    // wall-clock time is re-derived. Because the timeline AND the 3D highway
    // render notes/trails from `time_beats` + the LIVE bpm/offset, every note
    // and waypoint re-aligns onto the new grid automatically and LOSSLESSLY —
    // no destructive snap, no per-nudge rounding drift. We only refresh the
    // cached `time_seconds` so exporters / the timeline stay consistent.
    //
    // Reference check: an in-place tempo edit reuses the same notes array
    // (`setChart({ ...chart, bpm })`), whereas loading a file brings a brand-new
    // array — only the former is re-seated so a fresh load is left untouched.
    const tempoChanged =
      (chart.bpm !== prev.bpm || chart.offset_seconds !== prev.offset_seconds)
      && chart.bpm > 0;
    const inPlaceTempoEdit =
      chart.notes === prev.notes || chart.difficultyNotes === prev.difficultyNotes;

    if (tempoChanged && inPlaceTempoEdit) {
      const reseat = (notes: FlowNote[]): FlowNote[] =>
        notes.map(n => ({
          ...n,
          time_seconds: beatToTime(n.time_beats, chart.bpm, chart.offset_seconds),
        }));
      const baseDn = finalChart.difficultyNotes ?? {};
      const nextDn: Record<string, FlowNote[]> = {};
      for (const k of Object.keys(baseDn) as Array<keyof typeof baseDn>) {
        nextDn[k] = reseat(baseDn[k] ?? []);
      }
      finalChart = { ...finalChart, notes: reseat(finalChart.notes), difficultyNotes: nextDn };
    }

    set({ chart: finalChart, canUndo: true });
  },

  setActiveDifficultyAfterLoad: (d) => set({
    activeDifficulty: migrateFlowDifficultyName(d),
    selection: { noteIds: new Set() },
    cutNoteIds: new Set(),
    pendingTrailNoteId: null,
  }),

  editMapInfoOpen: false,
  setEditMapInfoOpen: (open) => set({ editMapInfoOpen: open }),

  setActiveDifficulty: (newDiff) => {
    const { chart, activeDifficulty } = get();
    if (newDiff === activeDifficulty) return;
    // Snapshot the whole chart so undo rewinds a difficulty switch in one shot.
    pushHistory(chart);
    const dn: Partial<Record<string, FlowNote[]>> = { ...(chart.difficultyNotes ?? {}) };
    // Park the active diff's current notes in the snapshot map.
    dn[activeDifficulty] = chart.notes;
    // Pull the new diff's notes (or empty if first time editing it) and
    // remove it from the snapshot map so there's no duplication.
    const incoming = dn[newDiff] ?? [];
    delete dn[newDiff];
    set({
      chart: { ...chart, notes: incoming, difficultyNotes: dn },
      activeDifficulty: newDiff,
      // Switching diffs invalidates whatever was selected.
      selection: { noteIds: new Set() },
      cutNoteIds: new Set(),
      pendingTrailNoteId: null,
      canUndo: true,
    });
  },

  // Add a difficulty (default or custom-named): switching to a not-yet-existing
  // difficulty creates it with empty notes, so this just delegates to the swap.
  addDifficulty: (name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    get().setActiveDifficulty(trimmed);
  },

  setActiveLaneCount: (count) => {
    const { chart, activeDifficulty } = get();
    const oldCount = laneCountFor(chart, activeDifficulty);
    if (count === oldCount) return;
    pushHistory(chart);
    const notes = remapNotesToLaneCount(chart.notes, oldCount, count);
    set({
      chart: {
        ...chart,
        notes,
        laneCounts: { ...(chart.laneCounts ?? {}), [activeDifficulty]: count },
      },
      // Lane remap can move/merge notes → clear selection & any pending trail.
      selection: { noteIds: new Set() },
      cutNoteIds: new Set(),
      pendingTrailNoteId: null,
      canUndo: true,
    });
  },

  addNote: (lane, time_beats, type = 'tap') => {
    const { chart } = get();
    // Cap stacking: at most 2 notes may share a beat (across lanes).
    if (notesAtBeat(chart.notes, time_beats) >= 2) return;
    pushHistory(chart);
    const time_seconds = beatToTime(time_beats, chart.bpm, chart.offset_seconds);
    const note: FlowNote = {
      id: makeId(),
      lane,
      time_beats,
      time_seconds,
      type,
      duration_beats: 0,
    };
    const notes = [...chart.notes];
    const idx = notes.findIndex(n => n.time_beats > time_beats);
    if (idx === -1) notes.push(note);
    else notes.splice(idx, 0, note);
    set({ chart: { ...chart, notes }, canUndo: true });
  },

  liveAddNote: (lane, time_beats, duration_beats, type = 'tap') => {
    const { chart } = get();
    // Same ≤2-per-beat cap as addNote (live-mapping taps included).
    if (notesAtBeat(chart.notes, time_beats) >= 2) return null;
    pushHistory(chart);
    const note: FlowNote = {
      id: makeId(),
      lane,
      time_beats,
      time_seconds: beatToTime(time_beats, chart.bpm, chart.offset_seconds),
      type,
      duration_beats: Math.max(0, duration_beats),
    };
    // Live notes sound on key-press/release (handled in FlowApp); mark this id
    // so the strike-line crossing detector doesn't play it a second time.
    liveHitSuppress.add(note.id);
    const notes = [...chart.notes];
    const idx = notes.findIndex(n => n.time_beats > time_beats);
    if (idx === -1) notes.push(note);
    else notes.splice(idx, 0, note);
    set({ chart: { ...chart, notes }, canUndo: true });
    return note.id;
  },

  liveExtendNote: (id, duration_beats) => {
    const { chart } = get();
    const dur = Math.max(0, duration_beats);
    if (dur <= 0) return;
    let changed = false;
    const notes = chart.notes.map(n => {
      if (n.id !== id) return n;
      changed = true;
      return { ...n, duration_beats: dur };
    });
    if (!changed) return;
    set({ chart: { ...chart, notes } });
  },

  insertNotesBatch: (rawNotes) => {
    const { chart } = get();
    if (rawNotes.length === 0) return;
    pushHistory(chart);
    const bpm = chart.bpm;
    const newNotes: FlowNote[] = rawNotes.map(raw => ({
      id: makeId(),
      lane: raw.lane,
      time_beats: raw.time_beats,
      time_seconds: beatToTime(raw.time_beats, bpm, chart.offset_seconds),
      type: raw.type ?? 'tap',
      duration_beats: 0,
    }));
    // Merge with existing notes, keeping the array sorted by time.
    const merged = [...chart.notes, ...newNotes].sort((a, b) => a.time_beats - b.time_beats);
    set({ chart: { ...chart, notes: merged }, canUndo: true });
  },

  removeNotes: (ids) => {
    const { chart } = get();
    pushHistory(chart);
    const idSet = new Set(ids);
    set({
      chart: { ...chart, notes: chart.notes.filter(n => !idSet.has(n.id)) },
      selection: { noteIds: new Set() },
      canUndo: true,
    });
  },

  clearChart: () => {
    const { chart } = get();
    pushHistory(chart);
    // Clear EVERY difficulty's notes — Clear is a wipe action; users would
    // expect the entire chart to be empty regardless of which diff is active.
    set({
      chart: { ...chart, notes: [], difficultyNotes: {} },
      selection: { noteIds: new Set() },
      cutNoteIds: new Set(),
      canUndo: true,
    });
  },

  beginEdit: () => {
    pushHistory(get().chart);
    set({ canUndo: true });
  },

  moveNote: (id, lane, time_beats) => {
    const { chart } = get();
    const bpm = chart.bpm;
    const updated = chart.notes.map(n => {
      if (n.id !== id) return n;
      const newTime = beatToTime(time_beats, bpm, chart.offset_seconds);
      const next: FlowNote = {
        ...n,
        lane,
        time_beats,
        time_seconds: newTime,
      };
      // If the note has a waypoints[] list, keep waypoint[0] in sync with
      // the head so the trail render path stays consistent. Other
      // waypoints' times shift by the same delta so the trail keeps its
      // shape and total duration.
      if (n.waypoints && n.waypoints.length > 0) {
        const dt = time_beats - n.time_beats;
        next.waypoints = n.waypoints.map((w, i) => i === 0
          ? { lane, time_beats }
          : { ...w, time_beats: w.time_beats + dt },
        );
        if (next.waypoints.length > 1) {
          const last = next.waypoints[next.waypoints.length - 1].time_beats;
          next.duration_beats = Math.max(0, last - time_beats);
        }
      }
      return next;
    });
    set({ chart: { ...chart, notes: updated } });
  },

  moveWaypoint: (noteId, index, lane, time_beats) => {
    const { chart } = get();
    const bpm = chart.bpm;
    const updated = chart.notes.map(n => {
      if (n.id !== noteId || !n.waypoints || index < 0 || index >= n.waypoints.length) return n;
      const nextWaypoints = n.waypoints.map((w, i) => i === index
        ? { lane, time_beats }
        : w,
      );
      // Keep waypoints sorted by time so the curve doesn't flip. Clamp the
      // moved waypoint between its neighbours so the user can't drag past
      // them.
      const prevT = index > 0 ? nextWaypoints[index - 1].time_beats : -Infinity;
      const nextT = index < nextWaypoints.length - 1 ? nextWaypoints[index + 1].time_beats : Infinity;
      const clampedT = Math.min(nextT - 1e-4, Math.max(prevT + 1e-4, time_beats));
      nextWaypoints[index] = { lane, time_beats: clampedT };

      const headTime = nextWaypoints[0].time_beats;
      const tailTime = nextWaypoints[nextWaypoints.length - 1].time_beats;
      const next: FlowNote = {
        ...n,
        waypoints: nextWaypoints,
        duration_beats: Math.max(0, tailTime - headTime),
      };
      // Index 0 is the head — also update the top-level lane / time fields
      // so they stay aligned with waypoints[0].
      if (index === 0) {
        next.lane = lane;
        next.time_beats = clampedT;
        next.time_seconds = beatToTime(clampedT, bpm, chart.offset_seconds);
      }
      return next;
    });
    set({ chart: { ...chart, notes: updated } });
  },

  audioUrl: null,
  audioName: null,
  audioLoading: false,
  audioError: null,
  audioFrequency: 44100,
  audioChannels: 2,
  audioSamples: 0,

  setAudio: ({
    url, name, bpm, offsetSeconds, durationSeconds,
    audioFrequency, audioChannels, audioSamples,
  }) => {
    const { chart } = get();
    const prev = get().audioUrl;
    if (prev && prev.startsWith('blob:')) {
      try { URL.revokeObjectURL(prev); } catch { /* ignore */ }
    }
    set({
      audioUrl: url,
      audioName: name,
      audioLoading: false,
      audioError: null,
      duration: durationSeconds,
      audioFrequency: audioFrequency ?? get().audioFrequency,
      audioChannels: audioChannels ?? get().audioChannels,
      audioSamples: audioSamples ?? Math.round(durationSeconds * (audioFrequency ?? 44100) * (audioChannels ?? 2)),
      chart: { ...chart, bpm, offset_seconds: offsetSeconds },
      currentTime: 0,
      currentBeat: 0,
    });
  },

  setAudioLoading: (loading) => set({ audioLoading: loading }),
  setAudioError: (msg) => set({ audioError: msg, audioLoading: false }),

  songName: '',
  setSongName: (name) => set({ songName: name }),
  songAuthor: '',
  setSongAuthor: (name) => set({ songAuthor: name }),

  coverUrl: null,
  coverName: null,
  coverMime: null,
  coverBytes: null,
  setCover: ({ url, name, mime, bytes }) => {
    const prev = get().coverUrl;
    if (prev && prev.startsWith('blob:')) {
      try { URL.revokeObjectURL(prev); } catch { /* ignore */ }
    }
    set({ coverUrl: url, coverName: name, coverMime: mime, coverBytes: bytes });
  },
  clearCover: () => {
    const prev = get().coverUrl;
    if (prev && prev.startsWith('blob:')) {
      try { URL.revokeObjectURL(prev); } catch { /* ignore */ }
    }
    set({ coverUrl: null, coverName: null, coverMime: null, coverBytes: null });
  },

  currentTime: 0,
  currentBeat: 0,
  playing: false,
  duration: 180,
  playbackSpeed: (() => { try { const v = parseFloat(localStorage.getItem('synapse:flow:speed') || ''); return Number.isFinite(v) && v > 0 ? v : 1; } catch { return 1; } })(),

  setCurrentTime: (t) => {
    const { chart } = get();
    const currentBeat = Math.max(0, timeToBeat(t, chart.bpm, chart.offset_seconds));
    set({ currentTime: Math.max(0, t), currentBeat });
  },
  setPlaying: (p) => set({ playing: p }),
  setDuration: (d) => set({ duration: d }),
  setPlaybackSpeed: (s) => { try { localStorage.setItem('synapse:flow:speed', String(s)); } catch { /* ignore */ } set({ playbackSpeed: s }); },

  seek: (t) => {
    // Seeking used to force-commit a pending trail ("stale trail" protection),
    // but that made the SCROLL WHEEL exit rail mode mid-build — and a long
    // rail REQUIRES wheeling the timeline forward (user report). The pending
    // trail now survives seeks: the >16-beat guard in addTrailWaypoint is
    // what actually prevents far-back ghost connections, so the trail stays
    // buildable no matter how you move the playhead while shaping it.
    const clamped = Math.max(0, t);
    const st = get();
    if (st.playTest) {
      // Scored run: it's a play-through — the song runs from 0:00 to the end
      // and nothing may move the playhead (wheel, timeline, loop jumps…).
      // The run seeks to 0:00 BEFORE flipping playTest on, so it never needs
      // to pass this guard itself.
      if (st.playTestScored) return;
      // Practice: jumping BACK replays that stretch, so the notes you already
      // hit (or missed) are live again.
      if (clamped < st.currentTime - 0.05) resetPlayTestRun();
    }
    const fn = _audioSeekFn;
    if (fn) {
      try { fn(clamped); } catch { /* audio not ready — silent */ }
    }
    const { chart } = get();
    const currentBeat = Math.max(0, timeToBeat(clamped, chart.bpm, chart.offset_seconds));
    set({ currentTime: clamped, currentBeat });
  },
  registerAudioSeek: (fn) => { _audioSeekFn = fn; },

  musicVolume: loadSettings<FlowMixerSettings>(SETTINGS_KEYS.mixer, DEFAULT_FLOW_MIXER).musicVolume,
  setMusicVolume: (v) => {
    const clamped = Math.max(0, Math.min(1, v));
    saveSettings(SETTINGS_KEYS.mixer, { musicVolume: clamped, sfxVolume: get().sfxVolume });
    set({ musicVolume: clamped });
  },
  sfxVolume: loadSettings<FlowMixerSettings>(SETTINGS_KEYS.mixer, DEFAULT_FLOW_MIXER).sfxVolume,
  setSfxVolume: (v) => {
    const clamped = Math.max(0, Math.min(1, v));
    saveSettings(SETTINGS_KEYS.mixer, { musicVolume: get().musicVolume, sfxVolume: clamped });
    set({ sfxVolume: clamped });
  },

  syncOffsetMs: loadSettings<FlowSyncSettings>(SETTINGS_KEYS.sync, DEFAULT_FLOW_SYNC).syncOffsetMs,
  setSyncOffsetMs: (ms) => {
    const clamped = Math.max(-500, Math.min(500, Math.round(ms)));
    saveSettings(SETTINGS_KEYS.sync, { syncOffsetMs: clamped });
    set({ syncOffsetMs: clamped });
  },

  offsetWarnAt: 0,
  warnOffsetPlacement: () => set({ offsetWarnAt: Date.now() }),
  editBlockWarnAt: 0,
  warnEditBlocked: () => set({ editBlockWarnAt: Date.now() }),

  cameraView: 'default',
  setCameraView: (v) => set({ cameraView: v }),
  // Per-view pose overrides, loaded from localStorage so edits persist.
  defaultCameraPosition: _camSaved.defaultPos, defaultCameraRotation: _camSaved.defaultRot,
  leftCameraPosition:    _camSaved.leftPos,    leftCameraRotation:    _camSaved.leftRot,
  rightCameraPosition:   _camSaved.rightPos,   rightCameraRotation:   _camSaved.rightRot,
  customCameraPosition:  _camSaved.customPos,  customCameraRotation:  _camSaved.customRot,
  setDefaultCameraPosition: (p) => { set({ defaultCameraPosition: p }); persistCamera(get); },
  setDefaultCameraRotation: (r) => { set({ defaultCameraRotation: r }); persistCamera(get); },
  setLeftCameraPosition:    (p) => { set({ leftCameraPosition: p });    persistCamera(get); },
  setLeftCameraRotation:    (r) => { set({ leftCameraRotation: r });    persistCamera(get); },
  setRightCameraPosition:   (p) => { set({ rightCameraPosition: p });   persistCamera(get); },
  setRightCameraRotation:   (r) => { set({ rightCameraRotation: r });   persistCamera(get); },
  setCustomCameraPosition:  (p) => { set({ customCameraPosition: p });  persistCamera(get); },
  setCustomCameraRotation:  (r) => { set({ customCameraRotation: r });  persistCamera(get); },
  resetCameraPreset: (preset) => {
    if (preset === 'default') set({ defaultCameraPosition: null, defaultCameraRotation: null });
    else if (preset === 'left') set({ leftCameraPosition: null, leftCameraRotation: null });
    else if (preset === 'right') set({ rightCameraPosition: null, rightCameraRotation: null });
    else set({ customCameraPosition: null, customCameraRotation: null });
    persistCamera(get);
  },
  clearCustomCamera: () => {
    set({ customCameraPosition: null, customCameraRotation: null });
    persistCamera(get);
  },
  saveCameraRequest: 0,
  requestSaveCustomCamera: () => set(s => ({ saveCameraRequest: s.saveCameraRequest + 1 })),
  // Capture a live free-orbit pose into the Custom view and switch to it.
  storeCustomCamera: (pos, rot) => {
    set({ customCameraPosition: pos, customCameraRotation: rot, cameraView: 'custom' });
    persistCamera(get);
  },

  snapDivision: (() => { try { const v = parseInt(localStorage.getItem('synapse:flow:snap') || '', 10); return Number.isFinite(v) && v >= 1 ? v : 8; } catch { return 8; } })(),
  setSnapDivision: (n) => { const v = Math.max(1, n); try { localStorage.setItem('synapse:flow:snap', String(v)); } catch { /* ignore */ } set({ snapDivision: v }); },
  flowNjs: (() => { try { const v = parseFloat(localStorage.getItem('synapse:flow:njs') || ''); return Number.isFinite(v) ? v : 1; } catch { return 1; } })(),
  setFlowNjs: (n) => { const v = Math.max(0.1, Math.min(6, n)); try { localStorage.setItem('synapse:flow:njs', String(v)); } catch { /* ignore */ } set({ flowNjs: v }); },
  keybindings: (() => {
    try {
      const raw = localStorage.getItem('synapse:flow:keybindings');
      const saved = raw ? JSON.parse(raw) : {};
      // Migration: the old newMap default (Ctrl+N) is Chrome-reserved and could
      // never fire — drop it from saved copies so the working default applies.
      if (saved.newMap === 'Ctrl+N') delete saved.newMap;
      // Migration: edit-window moved from bare E to Ctrl+E (reviewer ask —
      // toggle straight from the keyboard without colliding with typing).
      if (saved.editWindow === 'E') delete saved.editWindow;
      return { ...DEFAULT_KEYBINDINGS, ...saved };
    }
    catch { return { ...DEFAULT_KEYBINDINGS }; }
  })(),
  setKeybinding: (action, combo) => {
    const next = { ...get().keybindings, [action]: combo };
    try { localStorage.setItem('synapse:flow:keybindings', JSON.stringify(next)); } catch { /* ignore */ }
    set({ keybindings: next });
  },
  resetKeybindings: () => {
    try { localStorage.removeItem('synapse:flow:keybindings'); } catch { /* ignore */ }
    set({ keybindings: { ...DEFAULT_KEYBINDINGS } });
  },
  minimapCam: (() => {
    // `tilt` is now a downward pitch angle in degrees (was a raw look-at-Z that
    // could invert the view). Key bumped to v2 so old-format values are dropped.
    const def = { height: 7.4, posZ: 1.7, tilt: 40 };
    try { const raw = localStorage.getItem('synapse:flow:minimapCam2'); return { ...def, ...(raw ? JSON.parse(raw) : {}) }; }
    catch { return def; }
  })(),
  setMinimapCam: (partial) => {
    const next = { ...get().minimapCam, ...partial };
    try { localStorage.setItem('synapse:flow:minimapCam2', JSON.stringify(next)); } catch { /* ignore */ }
    set({ minimapCam: next });
  },
  resetMinimapCam: () => {
    const def = { height: 7.4, posZ: 1.7, tilt: 40 };
    try { localStorage.removeItem('synapse:flow:minimapCam2'); } catch { /* ignore */ }
    set({ minimapCam: def });
  },
  minimapCamWinOpen: false,
  setMinimapCamWinOpen: (open) => set({ minimapCamWinOpen: open }),
  settingsOpen: false,
  setSettingsOpen: (open) => set({ settingsOpen: open }),
  settingsTab: 'gameplay',
  setSettingsTab: (tab) => set({ settingsTab: tab }),
  metronomeEnabled: false,
  setMetronomeEnabled: (v) => set({ metronomeEnabled: v }),
  showComplexity: false,
  setShowComplexity: (v) => set({ showComplexity: v }),
  debugSync: false,
  setDebugSync: (v) => set({ debugSync: v }),
  loopRegion: null,
  setLoopRegion: (r) => set({ loopRegion: r && r.endBeat > r.startBeat ? r : null }),
  clearLoopRegion: () => set({ loopRegion: null }),
  selectedSection: null,
  setSelectedSection: (r) => set({ selectedSection: r && r.endBeat > r.startBeat ? r : null }),
  // Shortcut hint bar: users can close it outright (✕) and bring it back from
  // Settings → UI. Persisted so it stays closed across sessions.
  hintBarHidden: (() => { try { return localStorage.getItem('synapse:flow:hintHidden') === '1'; } catch { return false; } })(),
  setHintBarHidden: (v) => {
    try { localStorage.setItem('synapse:flow:hintHidden', v ? '1' : '0'); } catch { /* ignore */ }
    set({ hintBarHidden: v });
  },

  playTest: false,
  playTestScored: false,
  setPlayTest: (v, scored = false) => {
    resetPlayTestRun();
    set({ playTest: v, playTestScored: v && scored, playTestStats: { hits: 0, misses: 0 } });
  },
  playTestStats: { hits: 0, misses: 0 },
  bumpPlayTest: (kind) => {
    const s = get().playTestStats;
    set({ playTestStats: kind === 'hit'
      ? { hits: s.hits + 1, misses: s.misses }
      : { hits: s.hits, misses: s.misses + 1 } });
  },

  editWindowEnabled: (() => { try { return localStorage.getItem('synapse:flow:editWindow') === '1'; } catch { return false; } })(),
  setEditWindowEnabled: (v) => {
    try { localStorage.setItem('synapse:flow:editWindow', v ? '1' : '0'); } catch { /* ignore */ }
    set({ editWindowEnabled: v });
  },

  liveMapping: false,
  setLiveMapping: (v) => {
    liveHitSuppress.clear();
    liveLaneHeld.fill(false);
    set({ liveMapping: v });
  },
  activeDifficulty: 'Hard',
  // setActiveDifficulty is defined above so it can swap chart.notes between
  // the per-difficulty snapshot map and the live array.
  noteTypeBrush: 'tap',
  setNoteTypeBrush: (t) => {
    // Switching brushes mid-trail used to silently ABANDON the pending trail:
    // the waypoints stayed but commitTrail never ran, so the <2-waypoint
    // degrade and (crucially) the Mirror-mode twin were skipped — "mirror
    // doesn't work with rails". Commit the pending trail properly instead.
    if (get().pendingTrailNoteId) get().commitTrail();
    set({ noteTypeBrush: t });
  },

  // Mirror placement: when on, every note/trail placed in the editor is also
  // duplicated mirrored across the centre line (lane L → FLOW_LANE_COUNT-1-L).
  noteMirror: (() => { try { return localStorage.getItem('synapse:flow:mirror') === '1'; } catch { return false; } })(),
  setNoteMirror: (v) => { try { localStorage.setItem('synapse:flow:mirror', v ? '1' : '0'); } catch { /* ignore */ } set({ noteMirror: v }); },

  pendingTrailNoteId: null,
  startTrail: (noteId) => {
    // Seed the pending trail with a single-waypoint head. The waypoints
    // array is what makes this note participate in the multi-segment
    // render path; further clicks extend it.
    const { chart } = get();
    const head = chart.notes.find(n => n.id === noteId);
    if (!head) {
      set({ pendingTrailNoteId: null });
      return;
    }
    const updated = chart.notes.map(n => n.id === noteId
      ? { ...n, waypoints: [{ lane: n.lane, time_beats: n.time_beats }] }
      : n,
    );
    set({
      chart: { ...chart, notes: updated },
      pendingTrailNoteId: noteId,
    });
  },

  addTrailWaypoint: (lane, time_beats) => {
    const { chart, pendingTrailNoteId } = get();
    if (!pendingTrailNoteId) return;
    const head = chart.notes.find(n => n.id === pendingTrailNoteId);
    if (!head || !head.waypoints || head.waypoints.length === 0) return;
    const last = head.waypoints[head.waypoints.length - 1];
    // Reject duplicate-time waypoints — they collapse to a zero-length
    // segment which the runtime can't render.
    if (time_beats <= last.time_beats) return;
    // Stale-trail guard (reviewer bug: "a rail connected from far back in the
    // timeline to a new note"): a pending trail left armed from an earlier
    // spot must not swallow a click made bars away. A gap this big is never
    // one deliberate segment — close the old trail and start a fresh one at
    // the click, exactly as if the user had clicked with a clean slate.
    if (time_beats - last.time_beats > 16) {
      get().commitTrail();
      const id = get().liveAddNote(lane, time_beats, 0, 'tap');
      if (id) get().startTrail(id);
      return;
    }
    const nextWaypoints = [...head.waypoints, { lane, time_beats }];
    const newDuration = time_beats - head.waypoints[0].time_beats;

    // If an independent note sits at the new waypoint position (e.g. the
    // user clicked an existing tap to mark the trail end), absorb it in
    // this same atomic update so it is owned by — and deleted with — the
    // trail.  Tolerance: ±0.02 beats is well below any practical snap grid.
    const absorbNote = chart.notes.find(n =>
      n.id !== pendingTrailNoteId &&
      n.lane === lane &&
      Math.abs(n.time_beats - time_beats) < 0.02,
    );

    const updated = chart.notes
      .filter(n => !absorbNote || n.id !== absorbNote.id)   // remove absorbed
      .map(n => n.id === pendingTrailNoteId
        ? { ...n, waypoints: nextWaypoints, duration_beats: newDuration }
        : n,
      );
    pushHistory(chart);
    set({ chart: { ...chart, notes: updated }, canUndo: true });
  },

  removeTrailWaypoint: (noteId, wpIndex) => {
    const { chart } = get();
    const note = chart.notes.find(n => n.id === noteId);
    // Only INTERMEDIATE nodes are removable — the head and tail define the
    // trail's span (deleting those means deleting/reshaping the whole trail).
    if (!note || !note.waypoints || wpIndex <= 0 || wpIndex >= note.waypoints.length - 1) return;
    pushHistory(chart);
    const wps = note.waypoints.filter((_, i) => i !== wpIndex);
    const notes = chart.notes.map(n => n.id === noteId
      ? { ...n, waypoints: wps, duration_beats: wps[wps.length - 1].time_beats - wps[0].time_beats }
      : n,
    );
    set({ chart: { ...chart, notes }, canUndo: true });
  },

  commitTrail: () => {
    const { chart, pendingTrailNoteId, noteMirror } = get();
    if (!pendingTrailNoteId) return;
    const head = chart.notes.find(n => n.id === pendingTrailNoteId);
    // Need at least 2 waypoints for it to be an actual trail. Otherwise
    // strip the waypoints array so the note degrades to a plain tap.
    if (!head || !head.waypoints || head.waypoints.length < 2) {
      const cleaned = chart.notes.map(n => n.id === pendingTrailNoteId
        ? { ...n, waypoints: undefined, duration_beats: 0 }
        : n,
      );
      set({
        chart: { ...chart, notes: cleaned },
        pendingTrailNoteId: null,
      });
      return;
    }
    // Mirror mode → drop a second trail mirrored across the centre line.
    // Mirror within the ACTIVE lane count (3/4-lane charts differ), matching
    // the tap-placement mirror — the old fixed 4-lane mirror pushed 3-lane
    // trails onto a hidden lane. A centre-lane trail (its own mirror) and a
    // spot already holding a note at the mirrored head are skipped.
    if (noteMirror) {
      const M = laneCountFor(chart, get().activeDifficulty) - 1;
      const mirrorLane = (M - head.lane) as FlowLaneIndex;
      // The mirror-on-placement path already dropped a PLAIN twin at the
      // mirrored head the moment the trail started. That twin used to trip
      // this occupancy check, so the mirrored TRAIL was silently skipped —
      // the reviewers' "mirror doesn't work with rails". A plain (no-
      // waypoint) note sitting exactly on the mirrored head is that twin:
      // absorb it into the mirrored trail. Anything else keeps its spot.
      const twin = chart.notes.find(n =>
        n.id !== head.id && n.lane === mirrorLane
        && Math.abs(n.time_beats - head.time_beats) < 0.02);
      const blocked = twin && twin.waypoints && twin.waypoints.length > 0;
      if (mirrorLane !== head.lane && !blocked) {
        const mirrorNote: FlowNote = {
          id: makeId(),
          lane: mirrorLane,
          time_beats: head.time_beats,
          time_seconds: head.time_seconds,
          type: head.type,
          duration_beats: head.duration_beats,
          waypoints: head.waypoints.map(w => ({ lane: (M - w.lane) as FlowLaneIndex, time_beats: w.time_beats })),
        };
        pushHistory(chart);
        const notes = chart.notes.filter(n => !twin || n.id !== twin.id);
        const idx = notes.findIndex(n => n.time_beats > mirrorNote.time_beats);
        if (idx === -1) notes.push(mirrorNote); else notes.splice(idx, 0, mirrorNote);
        set({ chart: { ...chart, notes }, pendingTrailNoteId: null, canUndo: true });
        return;
      }
    }
    set({ pendingTrailNoteId: null });
  },

  cancelTrail: () => {
    // Throw away the head note (and any waypoints) when the user aborts.
    const { chart, pendingTrailNoteId } = get();
    if (!pendingTrailNoteId) {
      set({ pendingTrailNoteId: null });
      return;
    }
    pushHistory(chart);
    set({
      chart: { ...chart, notes: chart.notes.filter(n => n.id !== pendingTrailNoteId) },
      pendingTrailNoteId: null,
      canUndo: true,
    });
  },

  selection: { noteIds: new Set() },
  selectNotes: (ids, additive = false) => {
    const { selection } = get();
    const next = new Set(additive ? selection.noteIds : []);
    for (const id of ids) next.add(id);
    set({ selection: { noteIds: next } });
  },
  deselectNotes: (ids) => {
    const { selection } = get();
    if (selection.noteIds.size === 0 || ids.length === 0) return;
    const next = new Set(selection.noteIds);
    for (const id of ids) next.delete(id);
    set({ selection: { noteIds: next } });
  },
  clearSelection: () => set({ selection: { noteIds: new Set() } }),

  selectAllNotes: () => {
    const { chart } = get();
    set({ selection: { noteIds: new Set(chart.notes.map(n => n.id)) } });
  },

  deleteSelection: () => {
    const { chart, selection } = get();
    if (selection.noteIds.size === 0) return;
    pushHistory(chart);
    set({
      chart: { ...chart, notes: chart.notes.filter(n => !selection.noteIds.has(n.id)) },
      selection: { noteIds: new Set() },
      canUndo: true,
    });
  },

  clipboardCount: 0,
  copySelection: () => {
    const { chart, selection } = get();
    const picked = chart.notes.filter(n => selection.noteIds.has(n.id));
    // Deep-copy so later edits to the original notes don't leak in.
    _clipboard = picked.map(n => ({ ...n }));
    // A fresh copy invalidates any pending cut — they're different intents.
    set({ clipboardCount: _clipboard.length, cutNoteIds: new Set() });
  },

  cutNoteIds: new Set(),
  cutSelection: () => {
    const { chart, selection } = get();
    if (selection.noteIds.size === 0) return;
    const picked = chart.notes.filter(n => selection.noteIds.has(n.id));
    _clipboard = picked.map(n => ({ ...n }));
    set({
      clipboardCount: _clipboard.length,
      cutNoteIds: new Set(selection.noteIds),
    });
  },
  clearCut: () => set({ cutNoteIds: new Set() }),

  pasteAtPlayhead: () => {
    if (_clipboard.length === 0) return;
    const { chart, currentBeat, cutNoteIds } = get();
    pushHistory(chart);
    const earliest = Math.min(..._clipboard.map(n => n.time_beats));
    const offsetBeats = currentBeat - earliest;
    // Build new notes with fresh ids and shifted timing. Trails must carry their
    // waypoints through the paste (shifted by the same beat delta) — dropping
    // them pasted a trail as a broken straight hold ("ghost" content).
    const fresh: FlowNote[] = _clipboard.map(src => {
      const newBeat = Math.max(0, src.time_beats + offsetBeats);
      const shift = newBeat - src.time_beats;
      return {
        id: makeId(),
        lane: src.lane,
        time_beats: newBeat,
        time_seconds: beatToTime(newBeat, chart.bpm, chart.offset_seconds),
        type: src.type,
        duration_beats: src.duration_beats,
        ...(src.waypoints ? {
          waypoints: src.waypoints.map(w => ({ ...w, time_beats: w.time_beats + shift })),
        } : {}),
      };
    });
    // If we got here via Ctrl+X (cut), strip the originals before pasting
    // so the paste consumes the cut instead of duplicating.
    const surviving = cutNoteIds.size > 0
      ? chart.notes.filter(n => !cutNoteIds.has(n.id))
      : chart.notes;
    const { merged, kept } = mergeRespectingCaps(surviving, fresh);
    set({
      chart: { ...chart, notes: merged },
      selection: { noteIds: new Set(kept.map(n => n.id)) },
      cutNoteIds: new Set(),
      canUndo: true,
    });
  },

  pasteMirrorAtPlayhead: () => {
    if (_clipboard.length === 0) return;
    const { chart, currentBeat, cutNoteIds, activeDifficulty } = get();
    pushHistory(chart);
    const earliest = Math.min(..._clipboard.map(n => n.time_beats));
    const offsetBeats = currentBeat - earliest;
    // Mirror across the ACTIVE lane count's centre line (matches Mirror Mode).
    // Flick directions flip too: a left arrow pasted mirrored becomes a right.
    const M = laneCountFor(chart, activeDifficulty) - 1;
    const flipType = (t: FlowNote['type']): FlowNote['type'] =>
      t === 'arrow-left' ? 'arrow-right' : t === 'arrow-right' ? 'arrow-left' : t;
    const fresh: FlowNote[] = _clipboard.map(src => {
      const newBeat = Math.max(0, src.time_beats + offsetBeats);
      const shift = newBeat - src.time_beats;
      return {
        id: makeId(),
        lane: (M - src.lane) as FlowLaneIndex,
        time_beats: newBeat,
        time_seconds: beatToTime(newBeat, chart.bpm, chart.offset_seconds),
        type: flipType(src.type),
        duration_beats: src.duration_beats,
        ...(src.waypoints ? {
          waypoints: src.waypoints.map(w => ({
            lane: (M - w.lane) as FlowLaneIndex,
            time_beats: w.time_beats + shift,
          })),
        } : {}),
      };
    });
    const surviving = cutNoteIds.size > 0
      ? chart.notes.filter(n => !cutNoteIds.has(n.id))
      : chart.notes;
    const { merged, kept } = mergeRespectingCaps(surviving, fresh);
    set({
      chart: { ...chart, notes: merged },
      selection: { noteIds: new Set(kept.map(n => n.id)) },
      cutNoteIds: new Set(),
      canUndo: true,
    });
  },

  renameBookmark: (time_beats, label) => {
    const { chart } = get();
    const marks = chart.bookmarks ?? [];
    const target = marks.find(b => Math.abs(b.time_beats - time_beats) < 0.01);
    if (!target) return;
    pushHistory(chart);
    const bookmarks = marks.map(b => b === target ? { ...b, label: label || undefined } : b);
    set({ chart: { ...chart, bookmarks }, canUndo: true });
  },

  toggleBookmarkAtPlayhead: () => {
    const { chart, currentBeat } = get();
    const marks = chart.bookmarks ?? [];
    // Toggle: a bookmark within half a beat of the playhead is removed.
    const near = marks.find(b => Math.abs(b.time_beats - currentBeat) < 0.5);
    pushHistory(chart);
    const bookmarks = near
      ? marks.filter(b => b !== near)
      : [...marks, { time_beats: currentBeat }].sort((a, b) => a.time_beats - b.time_beats);
    set({ chart: { ...chart, bookmarks }, canUndo: true });
  },

  jumpBookmark: (dir) => {
    const { chart, currentBeat } = get();
    const marks = chart.bookmarks ?? [];
    if (marks.length === 0) return;
    // Epsilon keeps a repeat press from re-matching the bookmark we're on.
    const EPS = 0.01;
    const target = dir === 1
      ? marks.find(b => b.time_beats > currentBeat + EPS)
      : [...marks].reverse().find(b => b.time_beats < currentBeat - EPS);
    if (!target) return;
    get().seek(beatToTime(target.time_beats, chart.bpm, chart.offset_seconds));
  },

  shiftSelectionBy: (deltaBeats) => {
    const { chart, selection } = get();
    if (selection.noteIds.size === 0 || deltaBeats === 0) return;
    // Find the earliest selected note so we can clamp the move so it
    // doesn't push anyone below beat 0.
    let minBeat = Infinity;
    for (const n of chart.notes) {
      if (selection.noteIds.has(n.id) && n.time_beats < minBeat) minBeat = n.time_beats;
    }
    if (!isFinite(minBeat)) return;
    const effective = Math.max(deltaBeats, -minBeat);
    if (effective === 0) return;
    pushHistory(chart);
    const updated = chart.notes
      .map(n => selection.noteIds.has(n.id)
        ? { ...n, time_beats: n.time_beats + effective, time_seconds: beatToTime(n.time_beats + effective, chart.bpm, chart.offset_seconds) }
        : n,
      )
      .sort((a, b) => a.time_beats - b.time_beats);
    set({ chart: { ...chart, notes: updated }, canUndo: true });
  },

  shiftSelectionLane: (delta) => {
    const { chart, selection } = get();
    if (selection.noteIds.size === 0 || delta === 0) return;

    // Clamp delta so the whole selection stays within lanes 0..3 (considering
    // trail waypoints too), preserving each note's shape.
    let minLane = Infinity, maxLane = -Infinity;
    for (const n of chart.notes) {
      if (!selection.noteIds.has(n.id)) continue;
      const lanes = n.waypoints && n.waypoints.length > 0
        ? n.waypoints.map(w => w.lane)
        : [n.lane];
      for (const l of lanes) { if (l < minLane) minLane = l; if (l > maxLane) maxLane = l; }
    }
    if (!isFinite(minLane)) return;
    // Clamp within the ACTIVE lane count (a 4-lane chart must not nudge notes
    // onto the hidden 5th lane).
    const maxLaneIndex = laneCountFor(chart, get().activeDifficulty) - 1;
    const clamped = Math.max(-minLane, Math.min(delta, maxLaneIndex - maxLane));
    if (clamped === 0) return;

    pushHistory(chart);
    const updated = chart.notes.map(n => {
      if (!selection.noteIds.has(n.id)) return n;
      const lane = (n.lane + clamped) as FlowLaneIndex;
      const waypoints = n.waypoints
        ? n.waypoints.map(w => ({ ...w, lane: (w.lane + clamped) as FlowLaneIndex }))
        : n.waypoints;
      return { ...n, lane, waypoints };
    });
    set({ chart: { ...chart, notes: updated }, canUndo: true });
  },

  canUndo: false,
  undo: () => {
    const prev = _history.pop();
    if (!prev) return;
    // Park the CURRENT state on the redo stack so Ctrl+Y can bring it back.
    const cur = get();
    _redoStack.push({ chart: snapshotChart(cur.chart), activeDifficulty: cur.activeDifficulty });
    // Restoring also clears the selection and any pending cut — both
    // often reference notes that no longer exist after the rewind. The
    // active difficulty is restored too so undoing a diff switch puts
    // the UI back where it was.
    set({
      chart: prev.chart,
      activeDifficulty: prev.activeDifficulty,
      selection: { noteIds: new Set() },
      cutNoteIds: new Set(),
      pendingTrailNoteId: null,
      canUndo: _history.length > 0,
      canRedo: true,
    });
  },

  canRedo: false,
  redo: () => {
    const next = _redoStack.pop();
    if (!next) return;
    // Park the current state back on the undo stack (raw push — pushHistory
    // would wipe the redo branch we're consuming).
    const cur = get();
    _history.push({ chart: snapshotChart(cur.chart), activeDifficulty: cur.activeDifficulty });
    set({
      chart: next.chart,
      activeDifficulty: next.activeDifficulty,
      selection: { noteIds: new Set() },
      cutNoteIds: new Set(),
      pendingTrailNoteId: null,
      canUndo: true,
      canRedo: _redoStack.length > 0,
    });
  },

  // ── Visual settings (persisted to localStorage) ──────────────────────────────
  // Initialised from localStorage so they survive browser refreshes.
  // Every setter writes back immediately; resets also clear the stored value
  // back to defaults (so the next load starts fresh).

  gameplaySettings: (() => {
    const gs = loadSettings(SETTINGS_KEYS.gameplay, DEFAULT_FLOW_GAMEPLAY);
    // Older builds persisted 4 lane colours; pad to the 5-lane roster so the
    // 5th lane always has a colour (and the settings rows never see undefined).
    if (Array.isArray(gs.laneColors) && gs.laneColors.length < DEFAULT_FLOW_GAMEPLAY.laneColors.length) {
      gs.laneColors = [
        ...gs.laneColors,
        ...DEFAULT_FLOW_GAMEPLAY.laneColors.slice(gs.laneColors.length),
      ];
    }
    return gs;
  })(),
  setGameplaySettings: (patch) => {
    const next = { ...get().gameplaySettings, ...patch };
    saveSettings(SETTINGS_KEYS.gameplay, next);
    set({ gameplaySettings: next });
  },
  resetGameplaySettings: () => {
    saveSettings(SETTINGS_KEYS.gameplay, DEFAULT_FLOW_GAMEPLAY);
    set({ gameplaySettings: DEFAULT_FLOW_GAMEPLAY });
  },

  flowInterfaceSettings: loadSettings(SETTINGS_KEYS.interface, DEFAULT_FLOW_INTERFACE),
  setFlowInterfaceSettings: (patch) => {
    const next = { ...get().flowInterfaceSettings, ...patch };
    saveSettings(SETTINGS_KEYS.interface, next);
    set({ flowInterfaceSettings: next });
  },
  resetFlowInterfaceSettings: () => {
    saveSettings(SETTINGS_KEYS.interface, DEFAULT_FLOW_INTERFACE);
    set({ flowInterfaceSettings: DEFAULT_FLOW_INTERFACE });
  },

  particleSettings: loadSettings(SETTINGS_KEYS.particles, DEFAULT_FLOW_PARTICLES),
  setParticleSettings: (patch) => {
    const next = { ...get().particleSettings, ...patch };
    saveSettings(SETTINGS_KEYS.particles, next);
    set({ particleSettings: next });
  },
  resetParticleSettings: () => {
    saveSettings(SETTINGS_KEYS.particles, DEFAULT_FLOW_PARTICLES);
    set({ particleSettings: DEFAULT_FLOW_PARTICLES });
  },

  phoneSettings: loadSettings(SETTINGS_KEYS.phone, DEFAULT_FLOW_PHONE),
  setPhoneSettings: (patch) => {
    const next = { ...get().phoneSettings, ...patch };
    saveSettings(SETTINGS_KEYS.phone, next);
    set({ phoneSettings: next });
  },
  resetPhoneSettings: () => {
    saveSettings(SETTINGS_KEYS.phone, DEFAULT_FLOW_PHONE);
    set({ phoneSettings: DEFAULT_FLOW_PHONE });
  },

  visualizerScene: _vizSaved.scene,
  setVisualizerScene: (scene) => {
    set({ visualizerScene: scene });
    // Persist ONLY the single-scene source. When a timeline is loaded the scene
    // is just the transient active-clip snapshot the driver sets each change —
    // the timeline is the persisted source, so don't overwrite it here.
    if (!get().visualizerTimeline) {
      saveViz({ scene, timeline: null, projectName: null });
    }
  },

  visualizerTimeline: _vizSaved.timeline,
  visualizerProjectName: _vizSaved.projectName,
  setVisualizerTimeline: (clips, name = null) => {
    if (clips && clips.length > 0) {
      // Load a project timeline; the driver sets the active scene by time.
      set({ visualizerTimeline: clips, visualizerProjectName: name });
      saveViz({ scene: null, timeline: clips, projectName: name });
    } else {
      // Clear the timeline (and any scene the driver had set).
      set({ visualizerTimeline: null, visualizerProjectName: null, visualizerScene: null });
      saveViz({ scene: null, timeline: null, projectName: null });
    }
  },

  vizProject: null,
  setVizProject: (p) => {
    const prev = get().vizProject;
    if (prev && prev !== p) prev.dispose();   // revoke the old project's object-URLs
    set({ vizProject: p });
  },

  vizLibrary: [],
  setVizLibrary: (items) => set({ vizLibrary: items }),
  activeVizId: null,
  setActiveVizId: (id) => set({ activeVizId: id }),

  sfxSettings: loadSettings(SETTINGS_KEYS.sfx, DEFAULT_FLOW_SFX),
  setSfxSettings: (patch) => {
    const next = { ...get().sfxSettings, ...patch };
    saveSettings(SETTINGS_KEYS.sfx, next);
    set({ sfxSettings: next });
  },
  resetSfxSettings: () => {
    saveSettings(SETTINGS_KEYS.sfx, DEFAULT_FLOW_SFX);
    set({ sfxSettings: DEFAULT_FLOW_SFX });
  },
}));

// Console debug handle, same family as __synapseScene/__synapseBudget: lets a
// bug report be reproduced against the real store from devtools without
// adding throwaway instrumentation.
if (typeof window !== 'undefined') {
  (window as unknown as { __flowStore?: unknown }).__flowStore = useFlowStore;
}
